import { QueryClient } from '@tanstack/react-query';

// Enhanced QueryClient configuration for Phase 3 optimizations
export const createOptimizedQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Increase stale time for better caching
        staleTime: 5 * 60 * 1000, // 5 minutes
        // Cache data for longer periods
        gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in older versions)
        // Retry strategy for better reliability
        retry: (failureCount, error: any) => {
          // Don't retry 4xx errors
          if (error?.status >= 400 && error?.status < 500) {
            return false;
          }
          // Retry up to 3 times for other errors
          return failureCount < 3;
        },
        // Better refetch strategies
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: true,
      },
      mutations: {
        // Retry mutations on network errors
        retry: 1,
      },
    },
  });
};

// Query keys factory for consistent caching
export const queryKeys = {
  // Supplier related queries
  suppliers: {
    all: ['suppliers'] as const,
    featured: ['suppliers', 'featured'] as const,
    byId: (id: string) => ['suppliers', id] as const,
    products: (supplierId: string) => ['suppliers', supplierId, 'products'] as const,
    metrics: (supplierId: string) => ['suppliers', supplierId, 'metrics'] as const,
  },
  // Event related queries
  events: {
    all: ['events'] as const,
    byUser: (userId: string) => ['events', 'user', userId] as const,
    byId: (id: string) => ['events', id] as const,
    simulations: ['events', 'simulations'] as const,
  },
  // Product related queries
  products: {
    all: ['products'] as const,
    byCategory: (category: string) => ['products', 'category', category] as const,
    buffet: ['products', 'buffet'] as const,
    decorations: ['products', 'decorations'] as const,
    disposables: ['products', 'disposables'] as const,
    cakes: ['products', 'cakes'] as const,
    sweets: ['products', 'sweets'] as const,
  },
  // Admin and metrics
  admin: {
    metrics: ['admin', 'metrics'] as const,
    security: ['admin', 'security'] as const,
    suppliers: ['admin', 'suppliers'] as const,
  },
  // Tax and pricing
  tax: {
    checkout: ['tax', 'checkout'] as const,
    platform: ['tax', 'platform'] as const,
  },
  // Conversations and messages
  conversations: {
    all: ['conversations'] as const,
    byUser: (userId: string) => ['conversations', 'user', userId] as const,
    messages: (conversationId: string) => ['conversations', conversationId, 'messages'] as const,
  },
} as const;

// Cache management utilities
export const cacheUtils = {
  // Invalidate related queries when data changes
  invalidateSupplierData: (queryClient: QueryClient, supplierId?: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
    if (supplierId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.byId(supplierId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.products(supplierId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.metrics(supplierId) });
    }
  },
  
  invalidateEventData: (queryClient: QueryClient, eventId?: string, userId?: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.events.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.events.simulations });
    if (eventId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.byId(eventId) });
    }
    if (userId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.byUser(userId) });
    }
  },
  
  invalidateProductData: (queryClient: QueryClient, category?: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
    if (category) {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.byCategory(category) });
    }
  },
  
  // Prefetch commonly accessed data
  prefetchEssentialData: async (queryClient: QueryClient) => {
    // Prefetch featured suppliers (commonly viewed on homepage)
    await queryClient.prefetchQuery({
      queryKey: queryKeys.suppliers.featured,
      staleTime: 10 * 60 * 1000, // 10 minutes
    });
    
    // Prefetch tax data (needed for pricing calculations)
    await queryClient.prefetchQuery({
      queryKey: queryKeys.tax.checkout,
      staleTime: 30 * 60 * 1000, // 30 minutes
    });
    
    await queryClient.prefetchQuery({
      queryKey: queryKeys.tax.platform,
      staleTime: 30 * 60 * 1000, // 30 minutes
    });
  },
};